import router from '@/router'
import store from '@/store/index'
import axios from 'axios'
import Cookies from 'js-cookie'
import { jwtDecode } from 'jwt-decode'
import { captureUnexpectedError, getSupportScope } from '@/services/support-diagnostics'
import { INTERNET_CONNECTION_ERROR_MESSAGE, isNetworkConnectionError } from '@/services/internet-connection'
import { APP_UPDATED_NOTIFICATION_MESSAGE, showServerUpdateNotification } from '@/services/server-update-notification'
import getApiRoot from '@/services/api-root'

const apiClient = axios.create({
  baseURL: getApiRoot(),
  // timeout: 30000,
  withCredentials: true
})

const getCleanUrl = url => {
  try {
    const baseUrl = apiClient.defaults.baseURL
    const fullUrl = new URL(url, baseUrl)
    return fullUrl.pathname
  } catch (e) {
    return url.split('?')[0]
  }
}

const getSafeUrlPath = url =>
  getCleanUrl(url || '')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ':uuid')
    .replace(/\/\d+(?=\/|$)/g, '/:id')

const getCurrentRoute = () => {
  const currentRoute = router.currentRoute

  return currentRoute?.value || currentRoute
}

const getSafeRouteContext = () => {
  const route = getCurrentRoute()

  return {
    name: route?.name || 'unknown',
    path: getSafeUrlPath(route?.path || '')
  }
}

const getSafeErrorClass = error => error?.name || error?.code || 'Error'

const getSafeHttpReason = (error, response) => {
  if (error?.code === 'ECONNABORTED') {
    return 'request_timeout'
  }

  if (!response) {
    return 'network_or_cors_error'
  }

  if (response.status === 504) {
    return 'gateway_timeout'
  }

  if (response.status >= 500) {
    return 'server_error'
  }

  if (response.status >= 400) {
    return 'client_error'
  }

  return 'http_error'
}

const getSafeRequestAction = config => {
  const safeUrl = getSafeUrlPath(config?.url || '')
  const method = config?.method?.toUpperCase() || 'GET'

  return {
    safeUrl,
    method,
    action: `${method} ${safeUrl || 'unknown'}`
  }
}

const getSafeHttpContext = (error, response, config) => {
  const { safeUrl, method, action } = getSafeRequestAction(config)

  return {
    url: safeUrl || 'unknown',
    method,
    statusCode: response?.status || null,
    route: getSafeRouteContext(),
    action,
    reason: getSafeHttpReason(error, response),
    errorClass: getSafeErrorClass(error),
    axiosCode: error?.code || null
  }
}

const getSafeApiErrorCode = errorCode =>
  String(errorCode || 'unknown').replace(/[^a-zA-Z0-9_-]/g, '_')

const isRefreshEndpointRequest = config => {
  if (!config?.url) {
    return false
  }

  return getCleanUrl(config.url).includes('/api/token/refresh')
}

let isRefreshing = false
let isReloadingForVersionMismatch = false
let failedRequestsQueue = []
const pendingRequests = {}
const ignoredBusinessErrors = ['Associate not provided', "find the data ('Associate')"]
const ignoredBusinessErrorCodes = new Set(['associate_dnr_reentry_blocked'])
const ignoredBusinessErrorEndpoints = new Set([
  '/api/associate/get',
  '/api/associate/kpi/get',
  '/api/associate/schedule/gaps',
  '/api/scheduler_external/auth/generate_link',
  '/api/sign_in/verify/email',
  '/api/sign_in/verify/phone_number',
  '/api/shift/mark_paid',
  '/api/tc_get_time_attendance_detail_report'
])
const ignoredHttpErrorEndpoints = new Set([
  '/api/associate/clone',
  '/api/chat/message/send',
  '/api/project/update',
  '/api/associate/writeup/get_all',
  '/api/sequence/completed/get_by_id',
  '/api/point_system/action'
])
const ignoredHttpErrorSafePaths = new Set([
  '/api/project/notification/:id',
  '/api/time_off/:id',
  '/api/time_off/:id/items/shift/check',
  'api/project/notification/:id',
  'api/time_off/:id',
  'api/time_off/:id/items/shift/check'
])

const removePendingRequest = (key, abort = false) => {
  if (key && pendingRequests[key]) {
    if (abort) pendingRequests[key].abort()
    delete pendingRequests[key]
  }
}

const reloadForVersionMismatch = requiredVersion => {
  if (requiredVersion) {
    Cookies.set('version', requiredVersion, {
      expires: 3650
    })
  }

  if (isReloadingForVersionMismatch) {
    return
  }

  isReloadingForVersionMismatch = true
  window.location.reload()
}

const isIgnoredHttpErrorEndpoint = config => {
  if (config?.skipSentryHttpError) {
    return true
  }

  const cleanUrl = getCleanUrl(config?.url || '')

  return ignoredHttpErrorEndpoints.has(cleanUrl) || ignoredHttpErrorSafePaths.has(getSafeUrlPath(cleanUrl))
}

apiClient.interceptors.request.use(
  config => {
    if (config.supportScope === undefined) config.supportScope = getSupportScope()
    const timeZone = store.state.applicationState.timeZone
    if (timeZone) {
      config.headers['Client-Timezone'] = timeZone
    }

    const accessToken = Cookies.get('access')
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    const version = Cookies.get('version') || '1'

    if (version) {
      config.headers['web-build-number'] = `${version}`
    }

    const associate_id = store.state.applicationState?.associate?.id
    const project_id = store.state.applicationState?.currentProject?.id

    if (associate_id && !config.not_required) {
      config.headers['current-associate-id'] = associate_id
    }

    if (project_id) {
      config.headers['look-by-project-id'] = project_id
    }

    if (config?.cancelPreviousRequests && config?.url && !config.signal) {
      const cleanUrl = getCleanUrl(config.url)

      // Формируем уникальный ключ: URL + requestGroup (если указан)
      const requestGroup = config.requestGroup || '' // для обратной совместимости
      const pendingKey = requestGroup ? `${cleanUrl}__${requestGroup}` : cleanUrl

      config._pendingKey = pendingKey // Сохраняем ключ в конфиг для ответа

      // Отменяем предыдущий запрос ТОЛЬКО в рамках своей группы
      if (pendingRequests[pendingKey]) {
        removePendingRequest(pendingKey, true)
      }

      const abortController = new AbortController()
      config.signal = abortController.signal
      pendingRequests[pendingKey] = abortController
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

apiClient.interceptors.response.use(
  response => {
    if (response.config?._pendingKey) {
      removePendingRequest(response.config._pendingKey)
    }

    if (
      response.data.error_code &&
      response.data.error_code !== '0' &&
      response.data.error_code !== 111 &&
      response.data.error_code !== 90
    ) {
      const errorMessage = String(response.data.msg || '').trim()
      const businessErrorCode = getSafeApiErrorCode(response.data.error_code)
      const shouldIgnoreBusinessError = ignoredBusinessErrors.some(pattern => errorMessage.includes(pattern))
      const shouldIgnoreBusinessErrorCode = ignoredBusinessErrorCodes.has(businessErrorCode)
      const shouldIgnoreBusinessErrorEndpoint = ignoredBusinessErrorEndpoints.has(getCleanUrl(response.config?.url || ''))

      if (
        !response.config?.skipSentryBusinessError &&
        !shouldIgnoreBusinessError &&
        !shouldIgnoreBusinessErrorCode &&
        !shouldIgnoreBusinessErrorEndpoint
      ) {
        const { safeUrl, method, action } = getSafeRequestAction(response.config)

        captureUnexpectedError(new Error('API business error'), {
          contexts: {
            http: {
              url: safeUrl || 'unknown',
              method,
              statusCode: response.status || null,
              route: getSafeRouteContext(),
              action,
              reason: `api_error_code_${businessErrorCode}`,
              errorClass: 'ApiBusinessError'
            }
          },
          tags: {
            error_source: 'axios_interceptor',
            error_kind: 'business'
          }
        }, {
          source: response.data,
          description: response.data.msg,
          scope: response.config.supportScope
        })
      }
    }

    if (response.data.error_code === 90) {
      reloadForVersionMismatch(response.data.required_version)

      return new Promise(() => {})
    }

    if (response.data.error_code === 111) {
      if (!store.state.applicationState.needUpdate) {
        store.commit('setNeedUpdate', true)
      }
    }

    if (response && response?.status === 200) {
      if (getCurrentRoute()?.name === 'error') {
        router
          .push({
            name: 'info'
          })
          .catch(() => {})
      }
    }

    if (store.state.applicationState.isLoggingOut) {
      router.push('/auth').catch(() => {})
    }

    return response
  },
  async error => {
    const { response, config } = error
    let displayedError = error
    if (response?.status === 504) {
      displayedError = new Error(
        'The operation is taking too long, so we are canceling it. Please try reducing the data selection and try again!'
      )
    } else if (response?.status === 403) {
      displayedError = new Error('You do not have sufficient permissions to access this resource.')
    }

    // if (config?._pendingKey) {
    //   removePendingRequest(config._pendingKey);
    // }

    if (response?.status === 401 && (config?.skipAuthRefresh || isRefreshEndpointRequest(config))) {
      return Promise.reject(error)
    }

    if (isNetworkConnectionError(error)) {
      store.dispatch('checkInternetConnection').catch(() => {})

      return Promise.reject(new Error(INTERNET_CONNECTION_ERROR_MESSAGE))
    }

    if (
      error.code !== 'ERR_CANCELED' &&
      response?.status !== 503 &&
      response?.status !== 502 &&
      response?.status !== 401
    ) {
      const { safeUrl } = getSafeRequestAction(config)
      const sentryError = new Error(`Axios HTTP ${response?.status || 'unknown'}: ${safeUrl || 'unknown'}`)
      const shouldIgnoreHttpError = response?.status >= 400 && response?.status < 500 && isIgnoredHttpErrorEndpoint(config)

      if (shouldIgnoreHttpError) {
        return Promise.reject(error)
      }

      captureUnexpectedError(sentryError, {
        contexts: {
          http: getSafeHttpContext(error, response, config)
        },
        tags: {
          error_source: 'axios_interceptor',
          error_kind: getSafeHttpReason(error, response)
        }
      }, {
        source: displayedError,
        description: displayedError.message,
        scope: config?.supportScope
      })
    }

    if (!response || response.status !== 401 || config?._retry) {
      if (response?.status === 504) {
        return Promise.reject(displayedError)
      }

      if (response?.status === 503) {
        showServerUpdateNotification()

        return Promise.reject(new Error(APP_UPDATED_NOTIFICATION_MESSAGE))
      }

      if (response?.status === 403) {
        router
          .push({
            name: 'error',
            params: { errorType: 'no-permissions', errorCode: '403' }
          })
          .catch(() => {})
        return Promise.reject(displayedError)
      }

      return Promise.reject(error)
    }

    config._retry = true

    const refreshToken = Cookies.get('refresh')

    if (!refreshToken) {
      store.dispatch('logoutAndReset', {
        reason: 'axios_unauthorized_without_refresh',
        shouldCallSignOutApi: false,
        broadcast: true,
        redirectToAuth: true
      })

      return Promise.reject(error)
    }

    if (!isRefreshing) {
      isRefreshing = true

      try {
        const refreshResponse = await apiClient.post('/api/token/refresh', {
          refresh: refreshToken
        })

        const newAccessToken = refreshResponse.data.access
        const newRefreshToken = refreshResponse.data.refresh

        Cookies.set('access', newAccessToken, {
          expires: new Date(jwtDecode(newAccessToken).exp * 1000)
        })
        Cookies.set('refresh', newRefreshToken, {
          expires: new Date(jwtDecode(newRefreshToken).exp * 1000)
        })

        store.commit('setToken', newAccessToken)

        failedRequestsQueue.forEach(req => req.onSuccess(newAccessToken))
        failedRequestsQueue = []
      } catch (err) {
        failedRequestsQueue.forEach(req => req.onFailure(err))
        failedRequestsQueue = []

        const refreshTokenForSignOut = Cookies.get('refresh')
        const hadToken = Boolean(store.state.applicationState.token || Cookies.get('access'))

        store.dispatch('logoutAndReset', {
          reason: 'axios_refresh_failed',
          shouldCallSignOutApi: hadToken && Boolean(refreshTokenForSignOut),
          refreshToken: refreshTokenForSignOut,
          broadcast: true,
          redirectToAuth: true
        })

        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return new Promise((resolve, reject) => {
      failedRequestsQueue.push({
        onSuccess: token => {
          config.headers.Authorization = `Bearer ${token}`
          resolve(apiClient(config))
        },
        onFailure: err => {
          reject(err)
        }
      })
    })
  }
)

export default apiClient
